import * as React from 'react'
import { NavigationContainer, DefaultTheme } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { Platform, View, Dimensions } from 'react-native'
import { Text } from '../components/Themed'
import { FontAwesome5, MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons'
import LoginScreen from '../screens/Login'
import SettingsScreen from '../screens/Settings'
import OrdersScreen from '../screens/Orders'
import SignupScreen from '../screens/Signup'
import AccountScreen from '../screens/Account'
import { ReferScreen } from '../screens/Referral'
import WalletScreen from '../screens/Wallet'
import GlobalStyle, { SMALL_WIDTH, sTextSize } from '../constants/GlobalStyle'
import { APP_PREFIX, WEB_PREFIX } from '@env'
import ReceiptScreen from '../screens/Receipt'
import KioskHomeScreen from '../screens/KioskHome'
import Colors from '../constants/Colors'
import QRLoyaltyScreen from '../screens/QRLoyalty'
import ChefHatIcon from '../components/Rewards/ChefHatIcon'
import RewardsScreen from '../screens/Rewards'
import ResetPasswordScreen from '../screens/ResetPassword'
import DownloadScreen from '../screens/Download'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import CheckoutScreen from '../screens/Checkout'
import ItemScreen from '../screens/Item'
import RestaurantSelectScreen from '../screens/RestaurantSelect'
import RestaurantScreen from '../screens/Menu'
import GiftCardsScreen from '../screens/GiftCards'
import { setPersistedState } from '../state/Slices/persistedSlice'
import SlugItemScreen from '../screens/Item/SlugItem'
import { useOrganizationStyles } from '../hooks/useOrganizationStyles'
import PrivacyPolicyScreen from '../screens/PrivacyPolicy'
import TermsOfUseScreen from '../screens/TermsOfUse'
import { useEffect, useState } from 'react'
import RetrieveData from '../firebase/fbDataRetrieval/RetrieveData'
import { detectBot } from '../utils/botDetection'
import { useFirebaseAnalyticsContext } from '../context/FirebaseAnalyticsContext'
import { RetrieveDataProvider } from '../context/RetrieveDataContext'

const linking = {
	prefixes: [WEB_PREFIX],
	config: {
		screens: {
			HomeStack: {
				path: '',
				screens: {
					RestaurantSelect: {
						path: '/home',
					},
					Menu: '/menu/:rId',
					MenuBySlug: '/menu/:slug',
					Item: '/item/:token',
					MenuItemBySlug: {
						path: '/menu/:menu/:slug',
						parse: {
							menu: String,
							slug: String,
						},
					},
				},
			},
			AccountStack: {
				path: 'account',
				screens: {
					Account: {
						path: '',
					},
					Settings: '/settings',
					Wallet: '/wallet',
					Refer: '/refer',
					Login: '/login',
					Signup: '/signup',
				},
			},
			GiftCards: '/gift-cards',
			TabCheckout: '/cart',
			TabOrders: '/orders',
			receipt: '/receipt',
			download: '/download',
			QRLoyalty: '/rewards',
			Rewards: '/redeem',
			ResetPassword: '/reset-password/:token',
			PrivacyPolicy: '/privacy-policy',
			TermsOfUse: '/terms-of-use',
			Root: {
				parse: {
					referralCode: referralCode => referralCode,
				},
			},
		},
	},
}

// modifying DarkTheme/DefaultTheme in file wont change actual theme on web
const theme = {
	...DefaultTheme,
	colors: DefaultTheme.colors,
}

function Navigation({ colorScheme }) {
	const { loadedData } = useAppSelector(state => state.data)
	const { navigationStack } = useAppSelector(state => state.persisted)
	const dispatch = useAppDispatch()
	const { appBackground } = useOrganizationStyles()
	const [isOrgLoaded, setIsOrgLoaded] = useState(false)
	const { logScreenViewAnalytics } = useFirebaseAnalyticsContext()

	function buildAnalyticName(route) {
		const routeName = route.name
		const routeParams = route.params
		let analyticName = routeName
		if (routeParams) {
			if (routeName === 'Item') {
				analyticName = routeName + '_' + routeParams.token
			} else if (routeName === 'Menu') {
				analyticName = routeName + '_' + routeParams.rId
			} else if (routeName === 'MenuBySlug') {
				analyticName = routeName + '_' + routeParams.slug
			} else if (routeName === 'MenuItemBySlug') {
				analyticName = routeName + '_' + routeParams.menu + '_' + routeParams.slug
			}
		} else if (routeName === 'RestaurantSelect') {
			analyticName = 'Home'
		} else if (routeName === 'TabCheckout') {
			analyticName = 'Cart'
		} else if (routeName === 'TabOrders') {
			analyticName = 'Orders'
		}
		return analyticName
	}

	const navigationRef: any = React.useRef()
	const routeNameRef: any = React.useRef()

	const getPathFromState = state => {
		if (!state?.routes || state.routes.length === 0) return ''

		const lastRoute = state.routes[state.routes.length - 1]

		if (lastRoute.state) {
			return getPathFromState(lastRoute.state) //recursive check
		}

		const routePaths = {
			Menu: `/menu/${lastRoute.params?.rId}`,
			MenuBySlug: `/menu/${lastRoute.params?.slug}`,
			RestaurantSelect: '/home',
			Item: `/item/${lastRoute.params?.token}`,
			MenuItemBySlug: `/menu/${lastRoute.params?.menu}/${lastRoute.params?.slug}`,
		}

		return routePaths[lastRoute.name] || `/${lastRoute.name.toLowerCase()}`
	}

	const getInitialState = () => {
		if (!navigationStack || !window.location.pathname) return undefined

		const currentPath = window.location.pathname
		const expectedPath = getPathFromState(navigationStack)

		return currentPath === expectedPath ? navigationStack : undefined
	}

	useEffect(() => {
		if (!global.kiosk) {
			return
		}

		if (global.org) {
			setIsOrgLoaded(true)
		}
	}, [global.org])

	// Wrapper enables kiosk to retrieve live data of item availability
	return (
		<NavigationWrapper isOrgLoaded={isOrgLoaded}>
			<NavigationContainer
				linking={linking}
				theme={{ ...DefaultTheme, colors: { ...DefaultTheme.colors, background: appBackground } }}
				ref={navigationRef}
				initialState={Platform.OS === 'web' ? getInitialState() : null}
				onReady={() => {
					routeNameRef.current = navigationRef.current.getCurrentRoute()?.name
				}}
				onStateChange={state => {
					const previousRouteName = routeNameRef.current
					const currentRoute = navigationRef.current.getCurrentRoute()
					const currentRouteName = currentRoute?.name

					if (previousRouteName !== currentRouteName) {
						const screenName = buildAnalyticName(currentRoute)
						logScreenViewAnalytics(screenName)
					}

					dispatch(setPersistedState({ navigationStack: state }))

					routeNameRef.current = currentRouteName
				}}
			>
				<RootNavigator />
			</NavigationContainer>
		</NavigationWrapper>
	)
}

function NavigationWrapper({
	children,
	isOrgLoaded,
}: {
	children: React.ReactNode
	isOrgLoaded: boolean
}) {
	if (isOrgLoaded && global.kiosk) {
		return (
			<RetrieveDataProvider referralCode={''} loadingTracker={null}>
				<RetrieveData>{children}</RetrieveData>
			</RetrieveDataProvider>
		)
	}
	return children
}

const Stack = createNativeStackNavigator()

function RootNavigator() {
	const isKiosk = global.kiosk
	const isWeb = Platform.OS === 'web'
	const initialRoute = isKiosk ? 'KioskHome' : isWeb ? 'HomeStack' : 'tab'
	const isBot = detectBot()

	const { enableGiftCard } = useAppSelector(state => state.settings)
	const { appDisplayOptions } = useAppSelector(state => state.settings)

	const showTermsOfUse = appDisplayOptions?.showTermsOfUse
	return (
		<Stack.Navigator
			initialRouteName={initialRoute}
			screenOptions={{
				animation: 'none',
			}}
		>
			{isBot ? (
				<Stack.Screen
					name="HomeStack"
					component={HomeStackScreen}
					options={{ headerShown: false }}
				/>
			) : (
				<>
					{!isKiosk && !isWeb ? (
						<Stack.Screen
							name="tab"
							component={BottomTabNavigator}
							options={{ headerShown: false }}
						/>
					) : (
						<>
							<Stack.Screen
								name="AccountStack"
								component={AccountStackScreen}
								options={{ headerShown: false, title: 'Account' }}
							/>

							<Stack.Screen
								name="receipt"
								component={ReceiptScreen}
								options={{ headerShown: false }}
							/>
							<Stack.Screen
								name="download"
								component={DownloadScreen}
								options={{ headerShown: false }}
							/>

							<Stack.Screen
								name="ResetPassword"
								component={ResetPasswordScreen}
								options={{ headerShown: false }}
							/>

							<Stack.Screen
								name="TabOrders"
								component={OrdersScreen}
								options={{
									headerShown: false,
									title: 'Orders',
								}}
							/>

							<Stack.Screen
								name="TabCheckout"
								component={CheckoutScreen}
								options={{
									headerShown: false,
									title: 'Cart',
								}}
							/>

							{enableGiftCard && (
								<Stack.Screen
									name="GiftCards"
									component={GiftCardsScreen}
									options={{
										headerShown: false,
										title: 'Gift Cards',
									}}
								/>
							)}
							<Stack.Screen
								name="HomeStack"
								component={HomeStackScreen}
								options={{ headerShown: false }}
							/>
						</>
					)}

					<Stack.Screen
						name="QRLoyalty"
						component={QRLoyaltyScreen}
						options={{ headerShown: false, title: 'Rewards' }}
					/>

					<Stack.Screen
						name="Rewards"
						component={RewardsScreen}
						options={{ headerShown: false, title: 'Your Rewards' }}
					/>

					<Stack.Screen
						name="KioskHome"
						component={KioskHomeScreen}
						options={{ headerShown: false }}
					/>

					<Stack.Screen name="Item" component={ItemScreen} options={{ headerShown: false }} />
					{isWeb && (
						<Stack.Screen
							name="PrivacyPolicy"
							component={PrivacyPolicyScreen}
							options={{ headerShown: false }}
						/>
					)}

					{isWeb && showTermsOfUse && (
						<Stack.Screen
							name="TermsOfUse"
							component={TermsOfUseScreen}
							options={{ headerShown: false }}
						/>
					)}
				</>
			)}
		</Stack.Navigator>
	)
}

const CheckoutIcon = ({ color }) => {
	const { cart } = useAppSelector(state => state.persisted)

	const cartCount = cart.reduce((count, item) => count + item.qty, 0)

	return (
		<View>
			{cartCount > 0 ? (
				<View style={[GlobalStyle.fabTO3, { backgroundColor: global.orgColor }]}>
					<Text style={{ fontSize: 11, color: 'white' }}>{cartCount}</Text>
				</View>
			) : null}
			<MaterialCommunityIcons name="shopping-outline" size={26} color={color} />
		</View>
	)
}

const BottomTab = createBottomTabNavigator()

function BottomTabNavigator() {
	const { loadedData } = useAppSelector(state => state.data)
	const { isSingleBrand } = useAppSelector(state => state.settings)

	const loyaltyEnabled = loadedData[global.org].loyaltyEnabled || false

	return (
		<BottomTab.Navigator
			initialRouteName="tab"
			screenOptions={{
				tabBarActiveTintColor: global.orgColor,
				tabBarInactiveTintColor: global.orgColor2,
				tabBarLabelStyle: {
					fontSize: sTextSize,
					fontWeight: '600',
					fontFamily: Platform.OS === 'web' ? 'System' : 'DefaultFont',
				},
				tabBarStyle: {
					paddingTop: 10,
					paddingBottom: Platform.OS === 'ios' ? 20 : 10,
					shadowColor: '#000',
					shadowOffset: {
						width: 0,
						height: 1,
					},
					shadowOpacity: 0.12,
					shadowRadius: 4,
					elevation: 3,
					height:
						Platform.OS === 'ios'
							? Dimensions.get('window').height * 0.075 + 10
							: Dimensions.get('window').height * 0.075,
				},
			}}
			backBehavior="history"
		>
			<BottomTab.Screen
				name="HomeStack"
				component={HomeStackScreen}
				options={{
					title: isSingleBrand ? 'MENU' : 'HOME',
					tabBarIcon: ({ color }) => (
						<MaterialCommunityIcons
							name="silverware-fork-knife"
							size={24}
							color={color}
							style={{ marginLeft: -2 }}
						/>
					),
					headerShown: false,
				}}
			/>
			{loyaltyEnabled && (
				<BottomTab.Screen
					name="Rewards"
					component={RewardsScreen}
					options={{
						title: 'REWARDS',
						tabBarIcon: ({ color }) => <ChefHatIcon color={color} />,
						headerShown: false,
					}}
				/>
			)}

			<BottomTab.Screen
				name="TabOrders"
				component={OrdersScreen}
				options={{
					headerShown: false,
					title: 'ORDERS',
					tabBarIcon: ({ color }) => <FontAwesome5 name="history" size={22} color={color} />,
				}}
			/>

			<BottomTab.Screen
				name="TabCheckout"
				component={CheckoutScreen}
				options={{
					headerShown: false,
					title: 'CART',
					tabBarIcon: ({ color }) => <CheckoutIcon color={color} />,
				}}
			/>

			<BottomTab.Screen
				name="AccountStack"
				component={AccountStackScreen}
				options={{
					headerShown: false,
					title: 'ACCOUNT',
					tabBarIcon: ({ color }) => (
						<MaterialIcons name="account-circle" size={26} color={color} />
					),
				}}
			/>
		</BottomTab.Navigator>
	)
}

const HomeStack = createNativeStackNavigator()

function HomeStackScreen() {
	const { loadedData } = useAppSelector(state => state.data)
	const { isSingleBrand } = useAppSelector(state => state.settings)

	const sharedMenuParams =
		Platform.OS === 'web' && isSingleBrand ? { rId: loadedData['defaultRestaurant'].id } : null

	return (
		<HomeStack.Navigator
			screenOptions={{
				presentation: 'transparentModal',
				animation: 'slide_from_bottom',
			}}
		>
			{!isSingleBrand && (
				<HomeStack.Screen
					name="RestaurantSelect"
					component={RestaurantSelectScreen}
					options={{ headerShown: false, title: 'Home' }}
				/>
			)}

			<HomeStack.Screen
				name="Menu"
				component={RestaurantScreen}
				options={{ headerShown: false }}
				initialParams={sharedMenuParams}
			/>
			<HomeStack.Screen
				name="MenuBySlug"
				component={RestaurantScreen}
				options={{ headerShown: false, title: 'Menu' }}
				initialParams={sharedMenuParams}
			/>
			<Stack.Screen name="Item" component={ItemScreen} options={{ headerShown: false }} />
			<Stack.Screen
				name="MenuItemBySlug"
				component={ItemScreen}
				options={{ headerShown: false, title: 'Item' }}
			/>
		</HomeStack.Navigator>
	)
}

const AccountStack = createNativeStackNavigator()

function AccountStackScreen() {
	return (
		<AccountStack.Navigator
			screenOptions={{
				presentation: 'transparentModal',
				animation: 'slide_from_bottom',
			}}
		>
			<AccountStack.Screen
				name="Account"
				component={AccountScreen}
				options={{ headerShown: false }}
			/>
			<Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
			<Stack.Screen name="Signup" component={SignupScreen} options={{ headerShown: false }} />
			<AccountStack.Screen
				name="Settings"
				component={SettingsScreen}
				options={{ headerShown: false }}
			/>
			<AccountStack.Screen
				name="Wallet"
				component={WalletScreen}
				options={{ headerShown: false, presentation: 'card' }}
			/>
			<AccountStack.Screen name="Refer" component={ReferScreen} options={{ headerShown: false }} />
		</AccountStack.Navigator>
	)
}

export default Navigation
